import { GPUBenchmark } from "./GPUBenchmark";
import {
  type BenchmarkConfig,
  type BenchmarkResult,
  calculatePerformanceTier,
  DEFAULT_CONFIG,
  getScoreFromSubdiv,
  TIER_THRESHOLDS,
} from "./PerformanceTiers";

export class BenchmarkRunner {
  private _config: BenchmarkConfig;
  private _benchmark: GPUBenchmark;
  private _onProgress?: (progress: number, currentSubdiv: number) => void;
  private _onFrameCapture?: (subdiv: number, imageData: ImageData, frameTime: number) => void;

  constructor(
    config: Partial<BenchmarkConfig> = {},
    onProgress?: (progress: number, currentSubdiv: number) => void,
    onFrameCapture?: (subdiv: number, imageData: ImageData, frameTime: number) => void
  ) {
    this._config = { ...DEFAULT_CONFIG, ...config };
    this._benchmark = new GPUBenchmark(this._config.canvasSize, this._config.debug);
    this._onProgress = onProgress;
    this._onFrameCapture = onFrameCapture;
  }

  async run(): Promise<BenchmarkResult> {
    const startTime = performance.now();

    // Use custom thresholds if provided, otherwise use defaults
    const thresholds = this._config.thresholds || TIER_THRESHOLDS;

    // Create dynamic SUBDIV sequence based on thresholds
    const subdivSequence = [8, thresholds.MEDIUM, thresholds.HIGH, thresholds.ULTRA, thresholds.SUPERULTRA];

    // Find starting index in sequence
    let subdivIndex = subdivSequence.findIndex((val) => val >= this._config.startSubdiv);
    if (subdivIndex === -1) subdivIndex = 0;

    let currentSubdiv = subdivSequence[subdivIndex];
    let lastValidSubdiv = currentSubdiv;
    let totalFrameTime = 0;
    let frameCount = 0;

    try {
      // Warmup runs
      for (let i = 0; i < this._config.warmupRuns; i++) {
        await this._benchmark.renderFrame(currentSubdiv, this._config.framesPerTest);
      }

      // Main benchmark loop
      while (subdivIndex < subdivSequence.length && currentSubdiv <= this._config.maxSubdiv) {
        // Check timeout
        if (performance.now() - startTime > this._config.timeout) {
          console.warn("GPU benchmark timed out");
          break;
        }

        // Report progress based on position in sequence
        const progress = subdivIndex / (subdivSequence.length - 1);
        this._onProgress?.(progress, currentSubdiv);

        // Render frame and measure time
        const frameTime = await this._benchmark.renderFrame(currentSubdiv, this._config.framesPerTest);
        totalFrameTime += frameTime;
        frameCount++;

        // Capture frame if callback is provided (only on first subdivision run)
        if (this._onFrameCapture) {
          const imageData = this._benchmark.captureFrame();
          this._onFrameCapture(currentSubdiv, imageData, frameTime);
        }

        if (this._config.debug) {
          console.log(
            `SUBDIV ${currentSubdiv}: ${frameTime.toFixed(3)}ms per frame (${this._config.framesPerTest} frames total)`
          );
        }

        // Check if we've hit the performance limit
        if (frameTime > this._config.maxFrameTime) {
          if (this._config.debug) {
            console.log(`Frame time ${frameTime.toFixed(3)}ms exceeded limit ${this._config.maxFrameTime}ms`);
          }
          break;
        }

        // We successfully rendered at this subdivision level
        lastValidSubdiv = currentSubdiv;

        // Move to next subdivision in sequence
        subdivIndex++;
        if (subdivIndex < subdivSequence.length) {
          currentSubdiv = subdivSequence[subdivIndex];
        }

        // Brief yield to allow UI updates
        await new Promise((resolve) => setTimeout(resolve, 1));
      }

      // Calculate results
      const thresholds = this._config.thresholds || TIER_THRESHOLDS;
      const tier = calculatePerformanceTier(lastValidSubdiv, thresholds);
      const score = getScoreFromSubdiv(lastValidSubdiv, this._config.startSubdiv, thresholds);
      const averageFrameTime = totalFrameTime / frameCount;

      return {
        tier,
        score,
        maxSubdiv: lastValidSubdiv,
        averageFrameTime,
        timerType: this._benchmark.hasGPUTimer() ? "gpu" : "cpu",
      };
    } finally {
      this._benchmark.dispose();
    }
  }

  static async quickBenchmark(): Promise<BenchmarkResult> {
    const runner = new BenchmarkRunner({
      warmupRuns: 2,
      maxFrameTime: 16,
      timeout: 5000,
    });
    return runner.run();
  }
}
